import Button from "components/common/Button";
import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import SelectInput from "components/common/InputField/SelectInput";
import TextInput from "components/common/InputField/TextInput";
import Loader from "components/common/Loader";
import LoadingContent from "components/common/LoadingContent";
import ModalContainer from "components/common/ModalContainer";
import SpaceTaskCard from "components/common/SpaceTaskCard";
import TaskColumnContainer from "components/common/TaskColumnContainer";
import VendorAvatar from "components/common/VendorAvatar";
import { useMergedSearchParams } from "helpers/mergeSearchParams";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import useInfiniteScroll from "hooks/useInfinteScroll";
import { useNavigationHandler } from "hooks/useNavigationHandler";
import { debounce, truncate, uniqBy } from "lodash";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { spaceActions } from "redux/Ldb/actions/spaceActions";
import {
    Space,
    SpaceTask,
    SpaceTaskAssignedToKindEnum,
    SpaceTaskAssignee,
    SpaceTaskPriorityEnum,
    SpaceTaskReminderMethodEnum,
    SpaceTaskStatusEnum,
} from "redux/Ldb/types/spaceTypes";
import { normalizeMembers } from "utils/spaceUtils";
import SpaceTaskConversation from "./SpaceTaskConversation";
import TabsContainer from "components/common/TabsContainer";

const SpaceTaskDetails = ({
    taskId,
    setTaskId,
    type,
    setType,
    spaceData,
    handleRefetch,
}: {
    type: string;
    taskId: string;
    setTaskId: (taskId: string) => void;
    setType: (type: string) => void;
    spaceData: Space;
    handleRefetch: () => void;
}) => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const taskIdUrl = searchParams.get("taskId");
    const { spaceId } = useParams();
    const members = normalizeMembers(spaceData);
    const { goBack } = useNavigationHandler({ fallbackPath: `/spaces/${spaceId}` });
    const { updateSearchParams } = useMergedSearchParams();
    const [filter, setFilter] = useState<string>("");
    const [taskData, setTaskData] = useState<SpaceTask | null>(null);
    const [runsData, setRunsData] = useState<Record<string, any>[] | []>([]);
    const [status, setStatus] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const [updatedText, setUpdatedText] = useState<{ label: string; value: string } | null>(null);
    const [confirmDeleteTask, setConfirmDeleteTask] = useState<boolean>(false);
    const [updatedAssignees, setUpdatedAssignees] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const [pagination, setPagination] = useState<{ currentPage: string; noOfPages: string }>({
        currentPage: "1",
        noOfPages: "",
    });
    const [activeTab, setActiveTab] = useState<number>(0);
    const tabs = ["Run history", "Task Conversation"];

    const {
        fetchedSingleSpaceTaskSuccess,
        fetchingSingleSpaceTask,
        fetchedSpaceTasksRunsSuccess,
        fetchingSpaceTasksRuns,
        updatedSpaceTaskFailure,
        updatingSpaceTask,
        deletingSpaceTask,
        deletedSpaceTaskSuccess,
    } = useAppSelector((state) => state.space);

    const handleDeleteTask = () => {
        dispatch(spaceActions.deleteSpaceTask(spaceId, taskIdUrl));
    };

    console.log("members", members);

    useEffect(() => {
        if (Boolean(deletedSpaceTaskSuccess)) {
            setConfirmDeleteTask(false);
            setTaskId("");
            setType("");
            goBack();
        }
    }, [deletedSpaceTaskSuccess]);

    const handleUpdateTask = (name: string, value: string | string[]) => {
        if (name === "startAt" || name === "endAt") {
            dispatch(
                spaceActions.updateSpaceTask(spaceId, taskIdUrl, {
                    schedule: {
                        [name]: value,
                    },
                })
            );

            return;
        }
        dispatch(spaceActions.updateSpaceTask(spaceId, taskIdUrl, { [name]: value }));
    };

    const handleUpdateAssignees = (assignee: SpaceTaskAssignee) => {
        dispatch(spaceActions.updateSpaceTask(spaceId, taskIdUrl, { assignees: [assignee] }));
    };

    useEffect(() => {
        if (updatedText?.label) {
            dispatch(
                spaceActions.updateSpaceTask(spaceId, taskIdUrl, {
                    [updatedText?.label]: updatedText?.value,
                })
            );
        }
    }, [updatedText?.value]);

    const handleDebouncedChange = debounce((e) => {
        setUpdatedText({
            label: e?.target?.name,
            value: e?.target?.value,
        });
    }, 3000);

    const handleSearchChange = debounce((e) => {
        setDebouncedSearch(e?.target?.value);
    }, 2000);

    useEffect(() => {
        dispatch(spaceActions.fetchSingleSpaceTask(spaceId, taskIdUrl));
    }, [taskIdUrl]);

    useEffect(() => {
        if (Boolean(fetchedSingleSpaceTaskSuccess)) {
            setTaskData(fetchedSingleSpaceTaskSuccess?.task);
            const assignee = fetchedSingleSpaceTaskSuccess?.task?.assignees[0];
            const assigneeDetails =
                assignee?.kind !== "synth" &&
                members?.find(
                    (member) =>
                        member?.ldbId === assignee?.guestId || member?.ldbId === assignee?.ldbId
                );
            setUpdatedAssignees(
                assignee?.kind === "synth"
                    ? "Synth"
                    : assigneeDetails?.fullName ||
                          assigneeDetails?.email ||
                          assigneeDetails?.businessName
            );
            setType(fetchedSingleSpaceTaskSuccess?.task?.schedule?.type);
            dispatch(spaceActions.resetFetchSingleSpaceTask());
        }
    }, [fetchedSingleSpaceTaskSuccess, members]);

    const { updatedLimit, isLoadingMore, setLoadMore, updatedPage, setUpdatedPage } =
        useInfiniteScroll(
            {
                current: pagination.currentPage,
                number_of_pages: pagination.noOfPages,
            },
            15
        );

    useEffect(() => {
        if (type === "recurring" && taskIdUrl) {
            dispatch(
                spaceActions.fetchSpaceTasksRuns(
                    spaceId,
                    updatedPage,
                    15,
                    taskIdUrl,
                    status,
                    debouncedSearch,
                    type
                )
            );
        }
    }, [taskIdUrl, type, status, debouncedSearch]);

    useEffect(() => {
        if (Boolean(fetchedSpaceTasksRunsSuccess)) {
            setRunsData((prev) => {
                return updatedPage === 1
                    ? [...fetchedSpaceTasksRunsSuccess?.tasks]
                    : [...uniqBy([...prev, ...fetchedSpaceTasksRunsSuccess?.tasks], "_id")];
            });
            setPagination({
                currentPage: fetchedSpaceTasksRunsSuccess?.pagination?.current,
                noOfPages: fetchedSpaceTasksRunsSuccess?.pagination?.number_of_pages,
            });

            setLoadMore(false);
            dispatch(spaceActions.resetFetchSpaceTasksRuns());
        }
    }, [fetchedSpaceTasksRunsSuccess]);

    const taskStatusOptions = [
        {
            label: "To Do",
            value: "todo",
        },
        {
            label: "In Progress",
            value: "in_progress",
        },
        {
            label: "Completed",
            value: "completed",
        },
        {
            label: "Failed",
            value: "failed",
        },
        {
            label: "Cancelled",
            value: "canceled",
        },
    ];

    const taskPriorityOptions = [
        {
            label: "Low",
            value: "low",
        },

        {
            label: "Medium",
            value: "medium",
        },

        {
            label: "High",
            value: "high",
        },
    ];

    const displayActiveTab = () => {
        switch (activeTab) {
            case 0:
                return (
                    <div className="p-6">
                        <div className="text-sm">
                            <p className="text-slate-500 font-rocGroteskRegular">
                                This shows all past and ongoing runs for this task, including their
                                status, date, and results.
                            </p>
                        </div>
                        <div className="flex items-center space-x-2 mt-4 mb-6 max-sm:grid max-sm:space-x-0 max-sm:space-y-2 max-sm:grid-cols-1">
                            <TextInput
                                name={"search"}
                                value={search}
                                type={"text"}
                                inputPlaceholder={"Search runs"}
                                inputClassName={"!h-[38px] pl-[0px] !bg-white text-sm !mb-0"}
                                onChange={(e) => {
                                    setSearch(e?.target?.value);
                                }}
                                onInput={handleSearchChange}
                                containerClassname={"!w-full"}
                                leftIcon={
                                    <i className="ri-search-line before:content-['\f0d1] text-slate-400 mt-[2px]"></i>
                                }
                                inputContainerClassname={
                                    "!rounded-[4px] max-sm:!w-full !pl-[25px] !h-[40px] !bg-white !border !border-slate-200"
                                }
                            />

                            <Dropdown
                                name="filter"
                                value={""}
                                dropdownTriggerClassName={" "}
                                handleChange={(name, value) => {
                                    setStatus(value);
                                }}
                                dropdown={
                                    <div className="bg-white flex items-center max-sm:justify-between h-10 gap-2 py-2.5 px-3 shadow-boxShadow-2 border border-slate-200 !rounded">
                                        <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                            {status
                                                ? taskStatusOptions?.find(
                                                      (option) => option?.value === status
                                                  )?.label
                                                : "All"}
                                        </span>

                                        <div className="flex items-center space-x-2">
                                            {filter && (
                                                <i
                                                    onClick={(e) => {
                                                        e?.stopPropagation();
                                                        setFilter("");
                                                    }}
                                                    className="ri-close-circle-fill text-sm text-slate-500"
                                                ></i>
                                            )}
                                            <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                                        </div>
                                    </div>
                                }
                                wholeContainerClass={`max-sm:!w-full`}
                                dropdownItemsClasses=" !py-2"
                                dropdownClassName={`shadow-cardShadow-4 border border-slate-100 rounded !max-h-fit`}
                                dropdownContainerClasses={`shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)] -left-0  overflow-y-auto   border border-slate-200 !w-[159px] rounded !max-h-fit !py-0`}
                                dropdownOptions={[
                                    {
                                        label: "All",
                                        value: "",
                                    },
                                    ...taskStatusOptions,
                                ]}
                            />
                        </div>
                        {runsData?.length === 0 && (debouncedSearch || status) ? (
                            <div className="flex items-center justify-center flex-col gap-4 mt-20">
                                <p className="text-sm text-slate-500">No results found</p>
                            </div>
                        ) : runsData?.length === 0 ? (
                            <div className="flex items-center justify-center flex-col gap-4 mt-20">
                                <div className="flex flex-col items-center">
                                    <i className="ri-loop-right-line text-slate-950 text-lg"></i>
                                    <p className="text-sm text-slate-950">No runs yet</p>
                                </div>

                                <p className="text-sm text-slate-500  max-w-[294px]">
                                    This task hasn’t generated any runs. Once scheduled, you’ll see
                                    each run instance here with its name, description, and status.
                                </p>
                            </div>
                        ) : (
                            <TaskColumnContainer
                                title={"All runs"}
                                total={3}
                                borderColor={"#64748B"}
                                pagination={pagination}
                                setUpdatedPage={setUpdatedPage}
                                setLoadMore={setLoadMore}
                            >
                                <>
                                    {runsData?.map((task, idx) => {
                                        const name =
                                            task?.createdBy?.kind === "user"
                                                ? task?.createdBy?.firstName ||
                                                  task?.createdBy?.lastName ||
                                                  task?.createdBy?.ldb?.fullName ||
                                                  task?.createdBy?.guest?.email
                                                : "Synth";
                                        return (
                                            <SpaceTaskCard
                                                title={task?.name}
                                                description={truncate(task?.description, {
                                                    length: 60,
                                                })}
                                                startDate={task?.schedule?.startAt}
                                                endDate={task?.schedule?.endAt}
                                                createdBy={name}
                                                imageSrc={
                                                    task?.createdBy?.kind === "user"
                                                        ? task?.createdBy?.ldb?.profile?.avatar
                                                        : "https://storage.googleapis.com/heysynth_files/synth_webapp/synthAllylogo_fhimzk.png"
                                                }
                                                typeIcon={
                                                    task?.status === "todo" ? (
                                                        <i
                                                            className={`ri-checkbox-blank-circle-line text-[#64748B] text-base`}
                                                        ></i>
                                                    ) : task?.status === "in_progress" ? (
                                                        <i
                                                            className={`ri-hourglass-line text-[#FF8A00] text-base`}
                                                        ></i>
                                                    ) : task?.status === "completed" ? (
                                                        <i
                                                            className={`ri-check-line text-[#0AAD0A] text-base`}
                                                        ></i>
                                                    ) : task?.status === "canceled" ? (
                                                        <i
                                                            className={`ri-close-line text-[#E4281D] text-base`}
                                                        ></i>
                                                    ) : (
                                                        <i
                                                            className={`ri-close-line text-[#E4281D] text-base`}
                                                        ></i>
                                                    )
                                                }
                                                typeText={
                                                    task?.status
                                                        ? taskStatusOptions?.find(
                                                              (option) =>
                                                                  option?.value === task?.status
                                                          )?.label
                                                        : ""
                                                }
                                                typeColor={
                                                    task?.status === "todo"
                                                        ? "#64748B"
                                                        : task?.status === "in_progress"
                                                        ? "#FF8A00"
                                                        : task?.status === "completed"
                                                        ? "#0AAD0A"
                                                        : task?.status === "canceled"
                                                        ? "#E4281D"
                                                        : "#E4281D"
                                                }
                                            />
                                        );
                                    })}
                                    {fetchingSpaceTasksRuns && (
                                        <div className="flex justify-center my-4">
                                            <Loader color="gm-25" size={4} />
                                        </div>
                                    )}
                                </>
                            </TaskColumnContainer>
                        )}
                    </div>
                );

            case 1:
                return (
                    <div className="p-6">
                        <SpaceTaskConversation />
                    </div>
                );

            default:
                return;
        }
    };

    return (
        <div className="h-[calc(100%-21px)]">
            <LoadingContent loading={fetchingSingleSpaceTask}>
                <div className="flex flex-col h-full">
                    <div className="px-[24px] !sticky !top-0 bg-white z-10 flex items-center justify-between border-b border-b-slate-100">
                        <div
                            className="flex items-center gap-2 cursor-pointer"
                            onClick={() => {
                                updateSearchParams({
                                    taskId: "",
                                });
                                handleRefetch();
                                setType("");
                                setTaskId("");
                            }}
                        >
                            <i className="ri-arrow-left-line text-sm text-slate-950 cursor-pointer"></i>
                            <p className="text-sm font-rocGroteskMedium text-slate-950 cursor-pointer">
                                Back to tasklist
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <i className="ri-cloud-line text-sm text-slate-500"></i>
                            <p className="text-[13px] font-rocGroteskMedium text-slate-500">
                                {updatedSpaceTaskFailure
                                    ? "Not saved"
                                    : updatingSpaceTask
                                    ? "Saving..."
                                    : "Saved"}
                            </p>
                        </div>
                    </div>
                    <div
                        className={`font-rocGroteskMedium flex  h-full ${
                            type === "recurring" ? "flex-row" : "flex-col"
                        }`}
                    >
                        <div className="flex-1 min-h-0 overflow-y-auto pb-6 px-6">
                            <div
                                className={`flex  justify-center ${
                                    type === "recurring" ? "" : "items-center"
                                }`}
                            >
                                <div className="max-w-[640px] w-full text-start">
                                    <div className="mt-6 w-9 h-9 rounded-md bg-[#FFB902] flex items-center justify-center mb-8">
                                        <i className="ri-list-check-3 text-salte-950"></i>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="pb-4 border-b border-b-slate-100">
                                            <p className="text-xs font-rocGroteskMedium text-gm-45 mb-1">
                                                Task name
                                            </p>

                                            <input
                                                type="text"
                                                placeholder="Name"
                                                className=" font-rocGroteskMedium  placeholder:text-slate-400 !border-none outline-none w-full caret-slate-700 text-sm text-black"
                                                name="name"
                                                value={taskData?.name}
                                                onChange={(e) =>
                                                    setTaskData({
                                                        ...taskData,
                                                        name: e.target.value,
                                                    })
                                                }
                                                onInput={handleDebouncedChange}
                                            />
                                        </div>
                                        <div className="pb-4 border-b border-b-slate-100">
                                            <p className="text-xs font-rocGroteskMedium text-gm-45 mb-1">
                                                Description
                                            </p>
                                            <textarea
                                                placeholder="Description"
                                                className=" font-rocGroteskMedium  placeholder:text-slate-400 !border-none outline-none w-full caret-slate-700 text-sm text-black resize-none"
                                                name="description"
                                                value={taskData?.description}
                                                onChange={(e) =>
                                                    setTaskData({
                                                        ...taskData,
                                                        description: e.target.value,
                                                    })
                                                }
                                                onInput={handleDebouncedChange}
                                            />
                                        </div>
                                        <div className="pb-4 border-b border-b-slate-100">
                                            <p className="text-xs font-rocGroteskMedium text-gm-45 mb-1">
                                                Status
                                            </p>

                                            <div className="!w-fit">
                                                <Dropdown
                                                    name="status"
                                                    value={""}
                                                    dropdownTriggerClassName={" "}
                                                    handleChange={(name, value) => {
                                                        setTaskData({
                                                            ...taskData,
                                                            status: value as SpaceTaskStatusEnum,
                                                        });
                                                        handleUpdateTask(name, value);
                                                    }}
                                                    dropdown={
                                                        <div className="bg-white flex items-center max-sm:justify-between gap-2 py-2.5 px-3 !h-[32px] !w-fit !border !border-slate-200 !shadow-[0px_1px_4px_0px_rgba(0,0,0,0.05)] !rounded-[6px]">
                                                            <span className="h-2 w-2 rounded-full bg-[#EAB308]"></span>
                                                            <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                                                {
                                                                    taskStatusOptions?.find(
                                                                        (option) =>
                                                                            option?.value ===
                                                                            taskData?.status
                                                                    )?.label
                                                                }
                                                            </span>
                                                            <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                                                        </div>
                                                    }
                                                    wholeContainerClass={`max-sm:!w-full`}
                                                    dropdownItemsClasses=" !py-2"
                                                    dropdownClassName={`shadow-cardShadow-4 border border-slate-100 rounded !max-h-fit`}
                                                    dropdownContainerClasses={`shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)] -left-0  overflow-y-auto   border border-slate-200 !w-[159px] rounded !max-h-fit !py-0`}
                                                    dropdownOptions={taskStatusOptions}
                                                />
                                            </div>
                                        </div>
                                        <div className="pb-4 border-b border-b-slate-100">
                                            <p className="text-xs font-rocGroteskMedium text-gm-45 mb-1">
                                                Priority
                                            </p>
                                            <div className="!w-fit">
                                                <Dropdown
                                                    name="priority"
                                                    value={""}
                                                    dropdownTriggerClassName={" "}
                                                    handleChange={(name, value) => {
                                                        setTaskData({
                                                            ...taskData,
                                                            priority:
                                                                value as SpaceTaskPriorityEnum,
                                                        });
                                                        handleUpdateTask(name, value);
                                                    }}
                                                    dropdown={
                                                        <div className="bg-white flex items-center max-sm:justify-between gap-2 py-2.5 px-3 !h-[32px] !w-fit !border !border-slate-200 !shadow-[0px_1px_4px_0px_rgba(0,0,0,0.05)] !rounded-[6px]">
                                                            <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                                                {
                                                                    taskPriorityOptions?.find(
                                                                        (option) =>
                                                                            option?.value ===
                                                                            taskData?.priority
                                                                    )?.label
                                                                }
                                                            </span>
                                                            <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                                                        </div>
                                                    }
                                                    wholeContainerClass={`max-sm:!w-full`}
                                                    dropdownItemsClasses=" !py-2"
                                                    dropdownClassName={`shadow-cardShadow-4 border border-slate-100 rounded !max-h-fit`}
                                                    dropdownContainerClasses={`shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)] -left-0  overflow-y-auto   border border-slate-200 !w-[159px] rounded !max-h-fit !py-0`}
                                                    dropdownOptions={taskPriorityOptions}
                                                />
                                            </div>
                                        </div>
                                        <div className="pb-4 border-b border-b-slate-100">
                                            <p className="text-xs font-rocGroteskMedium text-gm-45 mb-1">
                                                Start date
                                            </p>
                                            <div className="!w-fit">
                                                <div className="flex items-center border border-slate-200 rounded-[6px] py-[4px] px-[6px] shadow-[0px_1px_4px_0px_rgba(0,0,0,0.05)]">
                                                    <TextInput
                                                        value={
                                                            taskData?.schedule?.startAt
                                                                ? new Date(
                                                                      taskData?.schedule?.startAt
                                                                  )
                                                                      ?.toISOString()
                                                                      ?.split("T")[0]
                                                                : ""
                                                        }
                                                        name={"startAt"}
                                                        type={"date"}
                                                        onChange={(e) => {
                                                            setTaskData({
                                                                ...taskData,
                                                                schedule: {
                                                                    ...taskData?.schedule,
                                                                    startAt: new Date(
                                                                        e?.target?.value
                                                                    ),
                                                                },
                                                            });
                                                            handleUpdateTask(
                                                                "startAt",
                                                                new Date(
                                                                    e?.target?.value
                                                                )?.toISOString()
                                                            );
                                                        }}
                                                        inputContainerClassname="!border-none !h-[24px] !p-0"
                                                        inputPlaceholder={"Start date"}
                                                        containerClassname="relative"
                                                        inputClassName=" !px-0 !py-0"
                                                        disabled={type === "recurring"}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="pb-4 border-b border-b-slate-100">
                                            <p className="text-xs font-rocGroteskMedium text-gm-45 mb-1">
                                                End date
                                            </p>
                                            <div className="!w-fit">
                                                <div className="flex items-center border border-slate-200 rounded-[6px] py-[4px] px-[6px] shadow-[0px_1px_4px_0px_rgba(0,0,0,0.05)]">
                                                    <TextInput
                                                        value={
                                                            taskData?.schedule?.endAt
                                                                ? new Date(
                                                                      taskData?.schedule?.endAt
                                                                  )
                                                                      ?.toISOString()
                                                                      ?.split("T")[0]
                                                                : ""
                                                        }
                                                        name={"endAt"}
                                                        type={"date"}
                                                        onChange={(e) => {
                                                            setTaskData({
                                                                ...taskData,
                                                                schedule: {
                                                                    ...taskData?.schedule,
                                                                    endAt: new Date(
                                                                        e?.target?.value
                                                                    ),
                                                                },
                                                            });
                                                            handleUpdateTask(
                                                                "endAt",
                                                                new Date(
                                                                    e?.target?.value
                                                                )?.toISOString()
                                                            );
                                                        }}
                                                        inputContainerClassname="!border-none !h-[24px] !p-0"
                                                        inputPlaceholder={"Start date"}
                                                        containerClassname="relative"
                                                        inputClassName=" !px-0 !py-0"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="pb-4 border-b border-b-slate-100">
                                            <p className="text-xs font-rocGroteskMedium text-gm-45 mb-1">
                                                Assigned to
                                            </p>
                                            <div className="!w-fit">
                                                <Dropdown
                                                    name="assignees"
                                                    value={""}
                                                    disabled={updatedAssignees === "Synth"}
                                                    dropdownTriggerClassName={" "}
                                                    handleChange={(name, value) => {
                                                        if (value === "synth") {
                                                            handleUpdateAssignees({
                                                                kind: "synth" as SpaceTaskAssignedToKindEnum.synth,
                                                            });
                                                            setUpdatedAssignees("Synth");
                                                        } else {
                                                            const type = value?.split("-")[0];
                                                            const id = value?.split("-")[1];
                                                            const name = value?.split("-")[2];

                                                            handleUpdateAssignees({
                                                                kind: "user" as SpaceTaskAssignedToKindEnum,
                                                                ...(type === "user"
                                                                    ? { ldbId: id }
                                                                    : { guestId: id }),
                                                            });
                                                            setUpdatedAssignees(name);
                                                        }
                                                    }}
                                                    dropdown={
                                                        <div className="bg-white flex items-center max-sm:justify-between gap-2 py-2.5 px-3 !h-[32px] !w-fit !border !border-slate-200 !shadow-[0px_1px_4px_0px_rgba(0,0,0,0.05)] !rounded-[6px]">
                                                            <span className="ri-user-line text-sm text-slate-500"></span>
                                                            <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                                                {updatedAssignees}
                                                            </span>
                                                            <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                                                        </div>
                                                    }
                                                    wholeContainerClass={`max-sm:!w-full`}
                                                    dropdownItemsClasses=" !py-2"
                                                    dropdownClassName={`shadow-cardShadow-4 border border-slate-100 rounded !max-h-fit`}
                                                    dropdownContainerClasses={`shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)] -left-0  overflow-y-auto   border border-slate-200 !w-[159px] rounded !max-h-fit !py-0`}
                                                    dropdownOptions={members?.map((member) => ({
                                                        label: member?.fullName
                                                            ? member?.fullName
                                                            : member?.email,
                                                        value: member?.isSynth
                                                            ? "synth"
                                                            : member.ldbId
                                                            ? `user-${member.ldbId}-${
                                                                  member.fullName || member.email
                                                              }`
                                                            : `guest-${member.guestId}-${member.email}`,
                                                        icon: (
                                                            <VendorAvatar
                                                                size={16}
                                                                imageSrc={member?.avatar}
                                                                name={
                                                                    member?.fullName
                                                                        ? member?.fullName
                                                                        : member?.email
                                                                }
                                                            />
                                                        ),
                                                    }))}
                                                />
                                            </div>
                                        </div>
                                        <div className="pb-4">
                                            <p className="text-sm font-rocGroteskMedium text-black mb-2">
                                                How should I remind you?
                                            </p>
                                            <div className="flex items-center gap-4">
                                                {[
                                                    { label: "Email", value: "email" },
                                                    {
                                                        label: "In-app notification",
                                                        value: "in_app_notification",
                                                    },
                                                ]?.map((item, idx) => {
                                                    return (
                                                        <div
                                                            className="flex items-center gap-2 text-slate-500"
                                                            onClick={() => {
                                                                const check =
                                                                    taskData?.reminderMethods
                                                                        ?.length > 0
                                                                        ? taskData?.reminderMethods
                                                                        : [];
                                                                if (
                                                                    check?.length > 0 &&
                                                                    check?.includes(
                                                                        item?.value as SpaceTaskReminderMethodEnum
                                                                    )
                                                                ) {
                                                                    const method = check?.filter(
                                                                        (method) =>
                                                                            method !== item?.value
                                                                    );
                                                                    setTaskData({
                                                                        ...taskData,
                                                                        reminderMethods:
                                                                            method as SpaceTaskReminderMethodEnum[],
                                                                    });
                                                                    handleUpdateTask(
                                                                        "reminderMethods",
                                                                        method
                                                                    );
                                                                } else {
                                                                    const method = [
                                                                        ...(taskData?.reminderMethods ||
                                                                            []),
                                                                        item?.value as SpaceTaskReminderMethodEnum,
                                                                    ];
                                                                    setTaskData({
                                                                        ...taskData,
                                                                        reminderMethods: method,
                                                                    });
                                                                    handleUpdateTask(
                                                                        "reminderMethods",
                                                                        method
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            {taskData?.reminderMethods?.includes(
                                                                item?.value as SpaceTaskReminderMethodEnum
                                                            ) ? (
                                                                <i className="ri-checkbox-fill text-lg text-[#FFB902] cursor-pointer"></i>
                                                            ) : (
                                                                <i className="ri-checkbox-blank-line text-lg cursor-pointer"></i>
                                                            )}

                                                            <p className="text-sm">{item?.label}</p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="flex justify-start gap-3">
                                            <Button
                                                btnText={"Copy link"}
                                                btnClassname="!w-fit !px-4 !py-2"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(
                                                        window.location.href
                                                    );
                                                    toast.custom((t) => (
                                                        <CustomToast
                                                            t={t}
                                                            title={"Link copied"}
                                                            message={
                                                                "The task link has been copied to your clipboard."
                                                            }
                                                            type={"success"}
                                                        />
                                                    ));
                                                }}
                                            />
                                            <Button
                                                btnText={"Delete task"}
                                                onClick={(e) => {
                                                    e?.stopPropagation();
                                                    setConfirmDeleteTask(true);
                                                }}
                                                type="button"
                                                btnClassname="!w-fit !bg-n-20 !text-gm-50 !px-4 !py-2"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* {type === "recurring" && (
                            <div className="w-[484px] max-w-[484px] min-h-0 overflow-y-auto border-l border-slate-100 ">
                                <TabsContainer
                                    tabs={tabs}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                    component={displayActiveTab()}
                                    itemClassName=""
                                    className="!px-6"
                                    disableTabParam={true}
                                />
                            </div>
                        )} */}
                        {type === "recurring" && (
                            <div className="w-[484px] max-w-[484px] min-h-0 overflow-y-auto border-l border-slate-100">
                                <div className="p-6">
                                    <div className="text-sm">
                                        <p className="text-black">Run history</p>
                                        <p className="text-slate-500 font-rocGroteskRegular">
                                            This shows all past and ongoing runs for this task,
                                            including their status, date, and results.
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-2 mt-4 mb-6 max-sm:grid max-sm:space-x-0 max-sm:space-y-2 max-sm:grid-cols-1">
                                        <TextInput
                                            name={"search"}
                                            value={search}
                                            type={"text"}
                                            inputPlaceholder={"Search runs"}
                                            inputClassName={
                                                "!h-[38px] pl-[0px] !bg-white text-sm !mb-0"
                                            }
                                            onChange={(e) => {
                                                setSearch(e?.target?.value);
                                            }}
                                            onInput={handleSearchChange}
                                            containerClassname={"!w-full"}
                                            leftIcon={
                                                <i className="ri-search-line before:content-['\f0d1] text-slate-400 mt-[2px]"></i>
                                            }
                                            inputContainerClassname={
                                                "!rounded-[4px] max-sm:!w-full !pl-[25px] !h-[40px] !bg-white !border !border-slate-200"
                                            }
                                        />

                                        <Dropdown
                                            name="filter"
                                            value={""}
                                            dropdownTriggerClassName={" "}
                                            handleChange={(name, value) => {
                                                setStatus(value);
                                            }}
                                            dropdown={
                                                <div className="bg-white flex items-center max-sm:justify-between h-10 gap-2 py-2.5 px-3 shadow-boxShadow-2 border border-slate-200 !rounded">
                                                    <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                                        {status
                                                            ? taskStatusOptions?.find(
                                                                  (option) =>
                                                                      option?.value === status
                                                              )?.label
                                                            : "All"}
                                                    </span>

                                                    <div className="flex items-center space-x-2">
                                                        {filter && (
                                                            <i
                                                                onClick={(e) => {
                                                                    e?.stopPropagation();
                                                                    setFilter("");
                                                                }}
                                                                className="ri-close-circle-fill text-sm text-slate-500"
                                                            ></i>
                                                        )}
                                                        <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                                                    </div>
                                                </div>
                                            }
                                            wholeContainerClass={`max-sm:!w-full`}
                                            dropdownItemsClasses=" !py-2"
                                            dropdownClassName={`shadow-cardShadow-4 border border-slate-100 rounded !max-h-fit`}
                                            dropdownContainerClasses={`shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)] -left-0  overflow-y-auto   border border-slate-200 !w-[159px] rounded !max-h-fit !py-0`}
                                            dropdownOptions={[
                                                {
                                                    label: "All",
                                                    value: "",
                                                },
                                                ...taskStatusOptions,
                                            ]}
                                        />
                                    </div>
                                    {runsData?.length === 0 && (debouncedSearch || status) ? (
                                        <div className="flex items-center justify-center flex-col gap-4 mt-20">
                                            <p className="text-sm text-slate-500">
                                                No results found
                                            </p>
                                        </div>
                                    ) : runsData?.length === 0 ? (
                                        <div className="flex items-center justify-center flex-col gap-4 mt-20">
                                            <div className="flex flex-col items-center">
                                                <i className="ri-loop-right-line text-slate-950 text-lg"></i>
                                                <p className="text-sm text-slate-950">
                                                    No runs yet
                                                </p>
                                            </div>

                                            <p className="text-sm text-slate-500  max-w-[294px]">
                                                This task hasn’t generated any runs. Once scheduled,
                                                you’ll see each run instance here with its name,
                                                description, and status.
                                            </p>
                                        </div>
                                    ) : (
                                        <TaskColumnContainer
                                            title={"All runs"}
                                            total={3}
                                            borderColor={"#64748B"}
                                            pagination={pagination}
                                            setUpdatedPage={setUpdatedPage}
                                            setLoadMore={setLoadMore}
                                        >
                                            <>
                                                {runsData?.map((task, idx) => {
                                                    const name =
                                                        task?.createdBy?.kind === "user"
                                                            ? task?.createdBy?.firstName ||
                                                              task?.createdBy?.lastName ||
                                                              task?.createdBy?.ldb?.fullName ||
                                                              task?.createdBy?.guest?.email
                                                            : "Synth";
                                                    return (
                                                        <SpaceTaskCard
                                                            title={task?.name}
                                                            description={truncate(
                                                                task?.description,
                                                                {
                                                                    length: 60,
                                                                }
                                                            )}
                                                            startDate={task?.schedule?.startAt}
                                                            endDate={task?.schedule?.endAt}
                                                            createdBy={name}
                                                            imageSrc={
                                                                task?.createdBy?.kind === "user"
                                                                    ? task?.createdBy?.ldb?.profile
                                                                          ?.avatar
                                                                    : "https://storage.googleapis.com/heysynth_files/synth_webapp/synthAllylogo_fhimzk.png"
                                                            }
                                                            typeIcon={
                                                                task?.status === "todo" ? (
                                                                    <i
                                                                        className={`ri-checkbox-blank-circle-line text-[#64748B] text-base`}
                                                                    ></i>
                                                                ) : task?.status ===
                                                                  "in_progress" ? (
                                                                    <i
                                                                        className={`ri-hourglass-line text-[#FF8A00] text-base`}
                                                                    ></i>
                                                                ) : task?.status === "completed" ? (
                                                                    <i
                                                                        className={`ri-check-line text-[#0AAD0A] text-base`}
                                                                    ></i>
                                                                ) : task?.status === "canceled" ? (
                                                                    <i
                                                                        className={`ri-close-line text-[#E4281D] text-base`}
                                                                    ></i>
                                                                ) : (
                                                                    <i
                                                                        className={`ri-close-line text-[#E4281D] text-base`}
                                                                    ></i>
                                                                )
                                                            }
                                                            typeText={
                                                                task?.status
                                                                    ? taskStatusOptions?.find(
                                                                          (option) =>
                                                                              option?.value ===
                                                                              task?.status
                                                                      )?.label
                                                                    : ""
                                                            }
                                                            typeColor={
                                                                task?.status === "todo"
                                                                    ? "#64748B"
                                                                    : task?.status === "in_progress"
                                                                    ? "#FF8A00"
                                                                    : task?.status === "completed"
                                                                    ? "#0AAD0A"
                                                                    : task?.status === "canceled"
                                                                    ? "#E4281D"
                                                                    : "#E4281D"
                                                            }
                                                        />
                                                    );
                                                })}
                                                {fetchingSpaceTasksRuns && (
                                                    <div className="flex justify-center my-4">
                                                        <Loader color="gm-25" size={4} />
                                                    </div>
                                                )}
                                            </>
                                        </TaskColumnContainer>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </LoadingContent>

            <ModalContainer
                open={confirmDeleteTask}
                modalClassName={"py-10 px-10"}
                tailwindClassName="sm:w-[500px] w-[90%] "
                showCloseIcon={false}
                closeModal={() => {
                    setConfirmDeleteTask(false);
                }}
            >
                <div className="bg-white py-7 px-6  shadow-cardShadow  rounded-[8px]">
                    <div className="mb-5 mt-4">
                        <div className="flex items-center justify-between">
                            <h1 className="text-base mb-2  font-rocGroteskMedium">Delete task?</h1>
                            <i
                                className="ri-close-line text-sm text-slate-500"
                                onClick={() => setConfirmDeleteTask(false)}
                            ></i>
                        </div>
                        <p className="text-slate-500 font-rocGroteskMedium text-sm">
                            Are you sure you want to permanently delete this task from this space?
                            This action cannot be undone.
                        </p>
                    </div>
                    <div className="border border-slate-200 rounded-[6px] py-4 px-3.5 flex mb-4 gap-2">
                        <div className=" w-9 h-9 rounded-md bg-[#FFB902] flex items-center justify-center">
                            <i className="ri-list-check-3 text-salte-950"></i>
                        </div>
                        <div className="">
                            <p className="text-[13px] text-[#142837] font-rocGroteskMedium">
                                {taskData?.name}
                            </p>
                            <p className="text-[13px] text-g-75 font-rocGroteskRegular">
                                {truncate(taskData?.description, {
                                    length: 40,
                                })}
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-end items-center ">
                        <div className="flex items-center !whitespace-nowrap space-x-2  ">
                            <Button
                                btnText={"No, cancel"}
                                type={"button"}
                                btnClassname={"!bg-n-20 !w-fit !text-gm-50"}
                                onClick={() => setConfirmDeleteTask(false)}
                            />
                            <Button
                                btnClassname={"!py-3 !w-fit !bg-[#E4281D] !text-white"}
                                onClick={() => {
                                    handleDeleteTask();
                                }}
                                btnText={"Delete task"}
                                type={"button"}
                                isLoading={deletingSpaceTask}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>
        </div>
    );
};

export default SpaceTaskDetails;
